import { DateTime } from 'luxon'
import { timePeriodsWithValues } from '@/constants/time-types'
/**
 * Sorts surveys by their end date.
 *
 * @param {Object} a - First survey object
 * @param {Object} b - Second survey object
 * @returns {number} - Comparison result
 */
export const sortSurveysByEndDate = (a, b) => {
  const aSurveyEndDate = a.details.aggregate
    ? a.details.aggregate.config.timeWindow.from_date ||
      DateTime.local().toISO()
    : a.details.end_date
  const bSurveyEndDate = b.details.aggregate
    ? b.details.aggregate.config.timeWindow.from_date ||
      DateTime.local().toISO()
    : b.details.end_date

  if (bSurveyEndDate > aSurveyEndDate) return 1
  else if (bSurveyEndDate < aSurveyEndDate) return -1
  else return 0
}

export const aggregateDates = ({ aggregate }) => {
  return datesForTimeWindow({ timeWindow: aggregate.config.timeWindow })
}

const datesForTimeWindow = ({ timeWindow }) => {
  const endDate = timeWindow.from_date
    ? DateTime.fromISO(timeWindow.from_date)
    : DateTime.local().endOf('day')
  const startDate = endDate.minus({
    [timeWindow.unit.toLowerCase()]: timeWindow.amount
  })
  return { endDate, startDate }
}

export const subtractTimeObjectFromSurveyDate = ({
  surveyDate,
  timeObject
}) => {
  // TODO: probably use survey end date here, right?
  // const today = new Date()
  // Create a new date instance to avoid mutating the original
  const result = new Date(surveyDate)
  if (timeObject.days) {
    result.setDate(result.getDate() - timeObject.days)
  } else if (timeObject.months) {
    result.setMonth(result.getMonth() - timeObject.months)
  } else if (timeObject.years) {
    result.setFullYear(result.getFullYear() - timeObject.years)
  }

  return result
}

export const timeObjectFromSelectedTimePeriod = ({ selectedTimePeriod }) => {
  // custom date is set in days
  if (selectedTimePeriod?.includes('d')) {
    return { days: parseInt(selectedTimePeriod, 10) }
  }

  return timePeriodsWithValues[selectedTimePeriod] || { years: 100 }
}

export const getTimePeriodSinceSurveyEndDate = ({
  surveyDetails,
  timePeriod
}) => {
  const endDateForSurvey = !!surveyDetails.details.aggregate
    ? surveyDetails.details.aggregate.config.timeWindow.from_date ||
      DateTime.local().toISO()
    : surveyDetails.details.end_date

  return DateTime.fromISO(endDateForSurvey).minus(timePeriod)
}

/**
 *
 * @param {Array} surveys - array of surveys to filter from
 * @param {Object} timeWindow - { startDate, endDate } in ISO string format, e.g 2023-09-07T11:49:54.758Z
 * @returns array of surveys
 */
export const surveysInTimeWindow = ({
  surveys,
  timeWindow,
  keepAggregates = false,
  limit
}) => {
  const endDate = DateTime.fromISO(timeWindow.endDate)
  const startDate = DateTime.fromISO(timeWindow.startDate)
  const filteredSurveys = surveys
    .filter((s) => {
      const surveyStartDate = DateTime.fromISO(s.details.start_date)
      const surveyEndDate = DateTime.fromISO(s.details.end_date)
      const surveyStartsBetweenStartAndEnd =
        surveyStartDate >= startDate && surveyStartDate <= endDate
      const surveyEndsBetweenStartAndEnd =
        surveyEndDate >= startDate && surveyEndDate <= endDate
      return (
        (!s.details.aggregate || keepAggregates) &&
        surveyStartsBetweenStartAndEnd &&
        surveyEndsBetweenStartAndEnd
      )
    })
    .sort(sortSurveysByEndDate)
  return limit && filteredSurveys.length >= limit
    ? filteredSurveys.slice(0, limit)
    : filteredSurveys
}

export const formatGlobalSurveyTimeWindow = ({
  timeWindow,
  format = 'short'
}) => {
  const formatMap = {
    short: DateTime.DATE_SHORT,
    medium: DateTime.DATE_MED
  }
  const startDate = DateTime.fromJSDate(timeWindow.startDate).toLocaleString(
    formatMap[format]
  )
  const endDate = DateTime.fromJSDate(timeWindow.endDate).toLocaleString(
    formatMap[format]
  )
  return { startDate, endDate }
}

const getBoundaryDate = (date, months) => {
  const d = new Date(date)
  // Using a little trick: set the month, JavaScript auto-handles year rollover
  d.setMonth(d.getMonth() - months)
  return d
}

export const surveyIsWithinTimePeriod = ({
  surveyDetails,
  timePeriodStartDate,
  surveyToCompareWith
}) => {
  const timePeriodEndDate = DateTime.fromISO(surveyDetails.details.end_date)
  const surveyEndDate = DateTime.fromISO(surveyToCompareWith.details.end_date)
  const surveyStartDate = DateTime.fromISO(
    surveyToCompareWith.details.start_date
  )
  if (!!surveyDetails.details.aggregate) {
    return surveyEndDate <= timePeriodEndDate
  } else {
    return (
      surveyStartDate >= timePeriodStartDate &&
      surveyEndDate <= timePeriodEndDate
    )
  }
}

/**
 * Get the first time period that covers all historical data, or the largest time period where the number of historical surveys are less than 10
 * If we make it all the way to 'all' there is either a valid time period or no historical data
 * If numberOfHistoricalSurveysInWindow is the same as historicalSurveys.length, return early
 * If there is no historical data, then we hide the history selector and don't need to set a time period
 */
export const findFirstTimePeriodWithData = ({
  surveyDetails,
  timePeriodOptions
}) => {
  const surveyEndDate = surveyDetails.details.end_date
  const historicalSurveys = surveyDetails.oldSurveysInfo

  let largestValidTimePeriod = null
  for (const period of timePeriodOptions.filter((o) => o.value !== 'custom')) {
    if (period.value === 'all' && largestValidTimePeriod) {
      return largestValidTimePeriod
    }
    // Extract the numeric part (e.g. '6' from '6m')
    const numberOfMonths = parseInt(period.value, 10)
    const boundary = getBoundaryDate(surveyEndDate, numberOfMonths)

    // Check if any end_date is within the boundary
    const numberOfHistoricalSurveysInWindow = historicalSurveys.filter(
      (survey) => {
        const endDate = new Date(survey.details.end_date)
        return endDate >= boundary
      }
    )?.length

    // if time period covers all historical data, return here and stop loop
    if (numberOfHistoricalSurveysInWindow === historicalSurveys.length) {
      return period
    }

    // update largestValidTimePeriod if the time period covers less than maxNumberOfHistoricalSurveys surveys
    const maxNumberOfHistoricalSurveys = 10
    if (numberOfHistoricalSurveysInWindow < maxNumberOfHistoricalSurveys) {
      largestValidTimePeriod = period
    }
  }
  return largestValidTimePeriod
}
